import { FocusMonitor } from '@angular/cdk/a11y'
import { coerceBooleanProperty } from '@angular/cdk/coercion'
import { Component, DoCheck, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self } from '@angular/core'
import { ControlValueAccessor, FormGroupDirective, NgControl, NgForm } from '@angular/forms'
import { MatFormFieldControl } from '@angular/material/form-field'
import { TranslatePipe } from '@ngx-translate/core'
import { isArray, isNil, isString, size } from 'lodash'
import { Subject } from 'rxjs'

@Component({
  selector: 'mmx-custom-form-field-control-cmp',
  template: '',
  providers: [TranslatePipe],
  standalone: false,
})
export class CustomMaterialInputControlComponent<T> implements MatFormFieldControl<T>, OnInit, DoCheck, OnDestroy, ControlValueAccessor {
  static nextId = 0

  public stateChanges = new Subject<void>()
  public focused = false
  public touched = false

  protected onChange: Function
  protected onTouched: Function
  protected _value: T
  protected _placeholder: string
  protected _required = false
  protected _disabled = false
  protected _name: string

  @HostBinding() id = `my-custom-field-${CustomMaterialInputControlComponent.nextId++}`
  @HostBinding('class.floating')
  get shouldLabelFloat() {
    return this.focused || !this.empty
  }
  @HostBinding('attr.aria-describedby') describedBy = ''
  setDescribedByIds(ids: string[]) {
    this.describedBy = ids.join(' ')
  }

  get empty() {
    return isNil(this.value) || (isArray(this.value) && size(this.value) === 0) || (isString(this.value) && this.value === '')
  }

  @Input()
  get value(): T {
    return this._value
  }
  set value(value: T) {
    this._value = value

    this.beforeChange()

    this.stateChanges.next()

    if (this.onChange) {
      this.onChange(this.value)
    }
  }

  @Input()
  get placeholder() {
    return this._placeholder
  }
  set placeholder(plh) {
    this._placeholder = plh
    this.stateChanges.next()
  }

  @Input()
  get required() {
    return this._required
  }
  set required(req) {
    this._required = coerceBooleanProperty(req)
    this.stateChanges.next()
  }

  @Input()
  get disabled() {
    return this._disabled
  }
  set disabled(dis) {
    this._disabled = coerceBooleanProperty(dis)
    this.stateChanges.next()
  }

  @Input()
  get name() {
    return this._name
  }
  set name(name) {
    this._name = name
    this.stateChanges.next()
  }

  errorState: boolean = false

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() protected _parentForm: NgForm,
    @Optional() protected _parentFormGroup: FormGroupDirective,
    protected fm: FocusMonitor,
    protected elRef: ElementRef,
  ) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this
    }
  }

  ngOnInit() {
    this.fm.monitor(this.elRef.nativeElement, true).subscribe((origin) => {
      const nextValue = !!origin

      if (this.focused && !nextValue) {
        // focusout event
        this.touched = true
      }
      this.focused = !!origin
      if (!nextValue && this.onTouched) {
        this.onTouched()
      }

      this.stateChanges.next()
    })
  }

  ngDoCheck() {
    if (this.ngControl) {
      this.updateErrorState()
    }
  }

  ngOnDestroy() {
    this.stateChanges.complete()
    this.fm.stopMonitoring(this.elRef.nativeElement)
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onContainerClick(event: MouseEvent) { }

  writeValue(value: T) {
    this.value = value
  }

  registerOnChange(fn: (value: T) => void) {
    this.onChange = fn
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled
  }

  protected beforeChange() { }

  protected updateErrorState() {
    const parent = this._parentFormGroup

    const oldState = this.errorState
    const newState = this.ngControl?.invalid && (this.touched || parent.submitted)

    if (oldState !== newState) {
      this.errorState = newState
      this.stateChanges.next()
    }
  }
}
